import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle, ArrowLeft, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope'] flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 -z-10 opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 20% 0%, rgba(255,193,7,0.12), transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,107,53,0.08), transparent 50%)' }} />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <div className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/10 p-8">
          <h1 className="text-2xl sm:text-3xl font-black mb-1">{title}</h1>
          {subtitle && <p className="text-sm text-[#C8C8C8] mb-6 leading-relaxed">{subtitle}</p>}
          {children}
        </div>
        {footer && <div className="mt-5 text-center text-sm text-[#A0A0A0]">{footer}</div>}
      </div>
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full pl-10 pr-11 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
        data-testid={testid}
      />
      <button type="button" onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ---------- LOGIN ----------
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const redirectTo = location.state?.from || '/mon-espace';

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Veuillez saisir votre email.'); return; }
    if (!password) { setError('Veuillez saisir votre mot de passe.'); return; }
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Connexion impossible.');
    } finally { setSubmitting(false); }
  }

  return (
    <AuthShell
      title="Connexion"
      subtitle="Connectez-vous pour publier ou gérer vos évènements ball-trap."
      footer={<>Pas encore de compte ? <Link to="/register" className="text-[#FFC107] hover:underline" data-testid="goto-register">Créer un compte</Link></>}
    >
      <form onSubmit={submit} autoComplete="on" className="space-y-3" data-testid="login-form">
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" autoComplete="email"
            className="w-full pl-10 pr-3 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
            data-testid="login-email" />
        </div>
        <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Mot de passe" autoComplete="current-password" testid="login-password" />
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2" data-testid="login-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </div>
        )}
        <button type="submit" disabled={submitting}
          className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="login-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {submitting ? 'Connexion…' : 'Se connecter'}
        </button>
        <div className="text-center pt-1">
          <Link to="/forgot-password" className="text-xs text-[#A0A0A0] hover:text-white" data-testid="goto-forgot">
            Mot de passe oublié ?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// ---------- REGISTER ----------
export function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accountCount, setAccountCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/stats`).then((r) => setAccountCount(r.data?.accounts_count ?? null)).catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Veuillez saisir votre email.'); return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.'); return;
    }
    setSubmitting(true);
    try {
      await register(email.trim().toLowerCase(), password, name.trim());
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Création de compte impossible.');
    } finally { setSubmitting(false); }
  }

  return (
    <AuthShell
      title="Créer un compte"
      subtitle={
        <>
          <strong className="text-[#FFC107]">100% gratuit · 30 secondes.</strong>
          {' '}Indispensable pour publier vos ball-traps de campagne et les gérer.
          {' '}<span className="inline-block mt-1.5">🎁 <strong className="text-[#FFC107]">7 jours Pro offerts</strong> sur l'application ClayPulse.</span>
        </>
      }
      footer={<>Déjà un compte ? <Link to="/login" className="text-[#FFC107] hover:underline" data-testid="goto-login">Se connecter</Link></>}
    >
      {accountCount !== null && accountCount > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/30 text-xs text-[#FFC107] text-center" data-testid="account-counter">
          🎯 <strong>{accountCount.toLocaleString('fr-FR')}</strong> {accountCount > 1 ? 'comptes créés' : 'compte créé'} · Rejoignez la communauté
        </div>
      )}
      <form onSubmit={submit} autoComplete="on" className="space-y-3" data-testid="register-form">
        <div className="relative">
          <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Nom (optionnel)" autoComplete="name"
            className="w-full pl-10 pr-3 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
            data-testid="register-name" />
        </div>
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" autoComplete="email"
            className="w-full pl-10 pr-3 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
            data-testid="register-email" />
        </div>
        <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Mot de passe (8 caractères minimum)" autoComplete="new-password" testid="register-password" />
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2" data-testid="register-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </div>
        )}
        <button type="submit" disabled={submitting}
          className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="register-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {submitting ? 'Création…' : 'Créer mon compte gratuit'}
        </button>
        <p className="text-[10px] text-[#888] text-center pt-1 leading-relaxed">
          En créant un compte, vous acceptez nos <Link to="/cgu" className="underline">CGU</Link> et notre <Link to="/privacy" className="underline">politique de confidentialité</Link>.
        </p>
      </form>
    </AuthShell>
  );
}

// ---------- FORGOT PASSWORD ----------
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Veuillez saisir votre email.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setError("Une erreur est survenue. Réessayez dans un instant.");
    } finally { setSubmitting(false); }
  }

  if (done) {
    return (
      <AuthShell title="Vérifiez votre boîte mail" footer={<Link to="/login" className="text-[#FFC107] hover:underline">Retour à la connexion</Link>}>
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-500/15 text-green-300 mx-auto mb-4">
          <CheckCircle className="w-7 h-7" />
        </div>
        <p className="text-sm text-[#C8C8C8] text-center leading-relaxed" data-testid="forgot-done">
          Si un compte existe pour <strong>{email}</strong>, un email avec un lien de réinitialisation vient d'être envoyé.
          Le lien est valable <strong>1 heure</strong>.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Mot de passe oublié"
      subtitle="Entrez votre email, on vous envoie un lien pour le réinitialiser."
      footer={<Link to="/login" className="text-[#FFC107] hover:underline">Retour à la connexion</Link>}
    >
      <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full pl-10 pr-3 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
            data-testid="forgot-email" />
        </div>
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
            {error}
          </div>
        )}
        <button type="submit" disabled={submitting}
          className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-50"
          data-testid="forgot-submit">
          {submitting ? 'Envoi…' : 'Envoyer le lien'}
        </button>
      </form>
    </AuthShell>
  );
}

// ---------- RESET PASSWORD ----------
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('8 caractères minimum.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou expiré.');
    } finally { setSubmitting(false); }
  }

  if (!token) {
    return (
      <AuthShell title="Lien invalide" footer={<Link to="/forgot-password" className="text-[#FFC107] hover:underline">Demander un nouveau lien</Link>}>
        <p className="text-sm text-[#C8C8C8] text-center">Aucun jeton de réinitialisation détecté. Demandez un nouveau lien.</p>
      </AuthShell>
    );
  }
  if (done) {
    return (
      <AuthShell title="Mot de passe modifié">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-500/15 text-green-300 mx-auto mb-4">
          <CheckCircle className="w-7 h-7" />
        </div>
        <p className="text-sm text-[#C8C8C8] text-center" data-testid="reset-done">Redirection vers la connexion…</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Nouveau mot de passe" subtitle="Choisissez un mot de passe d'au moins 8 caractères.">
      <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
        <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Nouveau mot de passe" autoComplete="new-password" testid="reset-password-input" />
        {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">{error}</div>}
        <button type="submit" disabled={submitting}
          className="w-full py-3 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-50"
          data-testid="reset-submit">
          {submitting ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </form>
    </AuthShell>
  );
}
